import { Injectable } from '@angular/core';
import { UserRepositoryService } from '../user/user-repository.service';
import { UserGroupRepositoryService } from '../user-group/user-group-repository.service';
import { OrderTypeAccessLevel } from '../../interfaces/order-type-access-levels/order-type-access-level';
import { KnowYourCustomerDefaults } from '../../interfaces/know-your-customer-defaults/know-your-customer-defaults';
import { of, lastValueFrom } from 'rxjs';
import { eAccessLevel, eOrderType, eOrderTypeGroup } from '../../enums';

@Injectable({
    providedIn: 'root'
})
export class ConfigurationState {
    private _isSet: boolean = false;
    private _isCorporationSet: boolean = false;
    private _corporationID: string = "";
    private _userGroupID: string = "";
    private _userID: string = "";
    private _username: string = "";
    private _billingDetailsID: string = "";
    private _hasAccountingAccess: boolean = false;
    private _hasUserManagementAccess: boolean = false;
    private _hasManageReusablePartyAccess: boolean = false;
    private _hasSystemAdminAccess: boolean = false;
    private _hasClientManagementAccess: boolean = false;
    private _hasBrandManagementAccess: boolean = false;
    private _hasAlertManagementAccess: boolean = false;
    private _defaultSumOfHypothecText: string | null = null;
    private _defaultSumOfHypothecPercent: number | null = null;
    private _currentDashboardFilter: string = "";
    private _currentDashboardLabel: string = "ALL";
    private _hasRegisteringAgentTabVisibility: boolean = false;
    private _hasOrderFeeVisibility: boolean = false;
    private _hasAlertsTabVisibility: boolean = false;
    private _orderTypeAccessLevels: OrderTypeAccessLevel[] = [];
    private _downloadJson: boolean = false;
    private _knowYourCustomerDefaults: KnowYourCustomerDefaults | null = null;

    constructor(private userRepo: UserRepositoryService,
        private userGroupRepo: UserGroupRepositoryService) { }

    // In-flight promises are cached so concurrent callers share one request instead of each hitting the API
    private _configurationPromise: Promise<boolean> | null = null;
    private _corporationPromise: Promise<boolean> | null = null;

    setConfiguration(): Promise<boolean> {
        if (!this._configurationPromise) {
            this._configurationPromise = this.loadConfiguration().catch(err => {
                this._configurationPromise = null;
                throw err;
            });
        }
        return this._configurationPromise;
    }

    private async loadConfiguration(): Promise<boolean> {
        if (this._isSet) {
            return lastValueFrom(of(this._isSet));
        }
        else {
            await lastValueFrom(this.userRepo.getAuthenticatedUser()).then(user => {
                this._isSet = true;
                this._userGroupID = user.userGroupID;
                this._userID = user.id;
                this._username = `${user.firstName} ${user.lastName}`
                this._hasAccountingAccess = user.configurationDetails?.hasAccountingAccess ?? false;
                this._hasUserManagementAccess = user.configurationDetails?.hasUserManagementAccess ?? false;
                this._hasManageReusablePartyAccess = user.configurationDetails?.hasManageReusablePartyAccess ?? false;
                this._hasSystemAdminAccess = user.configurationDetails?.hasSystemAdminAccess ?? false;
                this._hasClientManagementAccess = user.configurationDetails?.hasClientManagementAccess ?? false;
                this._hasBrandManagementAccess = user.configurationDetails?.hasBrandManagementAccess ?? false;
                this._hasAlertManagementAccess = user.configurationDetails?.hasAlertManagementAccess ?? false;
                this._defaultSumOfHypothecText = user.configurationDetails?.defaultSumOfHypothecText ?? null;
                this._defaultSumOfHypothecPercent = user.configurationDetails?.defaultSumOfHypothecPercent ?? null;
                this._hasRegisteringAgentTabVisibility = user.configurationDetails?.hasRegisteringAgentTabVisibility ?? false;
                this._hasOrderFeeVisibility = user.configurationDetails?.hasOrderFeeVisibility ?? false;
                this._hasAlertsTabVisibility = user.configurationDetails?.hasAlertsTabVisibility ?? false;
                this._orderTypeAccessLevels = user.configurationDetails?.orderTypeAccessLevels ?? [];
                this._downloadJson = user.configurationDetails?.downloadJson ?? false;
                this._knowYourCustomerDefaults = user.configurationDetails?.knowYourCustomerDefaults ?? null;
            });

            return this._isSet;
        }
    }

    setCorporation(): Promise<boolean> {
        if (!this._corporationPromise) {
            this._corporationPromise = this.loadCorporation().catch(err => {
                this._corporationPromise = null;
                throw err;
            });
        }
        return this._corporationPromise;
    }

    private async loadCorporation(): Promise<boolean> {
        if (this._isCorporationSet) {
            return lastValueFrom(of(this._isCorporationSet));
        }
        else {
            await this.setConfiguration().then(async _ => {
                await lastValueFrom(this.userGroupRepo.getUserGroup(this._userGroupID)).then(userGoup => {
                    this._isCorporationSet = true;
                    this._corporationID = userGoup.corporationID;
                    this._billingDetailsID = userGoup.billingDetailsID ?? "";
                });
            });

            return this._isCorporationSet;
        }
    }

    getCorporationID(): Promise<string> {
        return this.setCorporation().then(_ => {
            return this._corporationID;
        })
    }

    getUserGroupID(): Promise<string> {
        return this.setConfiguration().then(_ => {
            return this._userGroupID;
        })
    }

    getUserID(): Promise<string> {
        return this.setConfiguration().then(_ => {
            return this._userID;
        })
    }

    getUserName(): Promise<string> {
        return this.setConfiguration().then(_ => {
            return this._username;
        });
    }

    getBillingDetailsID(): Promise<string> {
        return this.setCorporation().then(_ => {
            return this._billingDetailsID;
        })
    }

    hasAccountingAccess(): Promise<boolean> {
        return this.setConfiguration().then(_ => {
            return this._hasAccountingAccess;
        })
    }

    hasUserManagementAccess(): Promise<boolean> {
        return this.setConfiguration().then(_ => {
            return this._hasUserManagementAccess;
        })
    }

    hasManageReusablePartyAccess(): Promise<boolean> {
        return this.setConfiguration().then(_ => {
            return this._hasManageReusablePartyAccess;
        })
    }

    hasSystemAdminAccess(): Promise<boolean> {
        return this.setConfiguration().then(_ => {
            return this._hasSystemAdminAccess;
        })
    }

    hasClientManagementAccess(): Promise<boolean> {
        return this.setConfiguration().then(_ => {
            return this._hasClientManagementAccess;
        })
    }

    hasBrandManagementAccess(): Promise<boolean> {
        return this.setConfiguration().then(_ => {
            return this._hasBrandManagementAccess;
        })
    }

    hasAlertManagementAccess(): Promise<boolean> {
        return this.setConfiguration().then(_ => {
            return this._hasAlertManagementAccess;
        })
    }

    getDefaultSumOfHypothecText(): Promise<string | null> {
        return this.setConfiguration().then(_ => {
            return this._defaultSumOfHypothecText;
        })
    }

    getDefaultSumOfHypothecPercent(): Promise<number | null> {
        return this.setConfiguration().then(_ => {
            return this._defaultSumOfHypothecPercent;
        })
    }

    hasRegisteringAgentTabVisibility(): Promise<boolean> {
        return this.setConfiguration().then(_ => {
            return this._hasRegisteringAgentTabVisibility;
        })
    }

    hasOrderFeeVisibility(): Promise<boolean> {
        return this.setConfiguration().then(_ => {
            return this._hasOrderFeeVisibility;
        })
    }

    hasAlertsTabVisibility(): Promise<boolean> {
        return this.setConfiguration().then(_ => {
            return this._hasAlertsTabVisibility;
        })
    }

    canDownloadJson(): Promise<boolean> {
        return this.setConfiguration().then(_ => {
            return this._downloadJson;
        })
    }

    getKnowYourCustomerDefaults(): Promise<KnowYourCustomerDefaults | null> {
        return this.setConfiguration().then(_ => {
            return this._knowYourCustomerDefaults;
        })
    }

    getOrderTypeAccessLevels(): Promise<OrderTypeAccessLevel[]> {
        return this.setConfiguration().then(_ => {
            return this._orderTypeAccessLevels;
        })
    }

    getOrderTypeAccessLevel(orderTypeID: eOrderType): Promise<eAccessLevel> {
        return this.setConfiguration().then(_ => {
            return this._orderTypeAccessLevels.find(access => access.orderTypeID == orderTypeID)?.accessLevelID ?? eAccessLevel.Full;
        })
    }

    getOrderTypeGroupAccessLevels(orderTypeGroupID: eOrderTypeGroup): Promise<eAccessLevel[]> {
        return this.setConfiguration().then(_ => {
            return this._orderTypeAccessLevels
                .filter(access => access.orderTypeGroupID == orderTypeGroupID)
                .map(access => access.accessLevelID) ?? [];
        })
    }

    canViewOrderType(orderTypeID: eOrderType): Promise<boolean> {
        return this.setConfiguration().then(_ => {
            return this.hasViewAccess(orderTypeID);
        })
    }

    //Any Order Types within the group have View Access
    canViewOrderTypeGroup(orderTypeGroup: eOrderTypeGroup): Promise<boolean> {
        let viewAccessLevels: eAccessLevel[] = [eAccessLevel.ViewOnly, eAccessLevel.DraftOnly, eAccessLevel.Full];
        return this.getOrderTypeGroupAccessLevels(orderTypeGroup).then(accesses => {
            return (accesses.length == 0 || //No defined Access Levels
                accesses.filter(access => viewAccessLevels.includes(access)).length > 0); //At least view access level
        });
    }

    canDraftOrderType(orderTypeID: eOrderType): Promise<boolean> {
        return this.setConfiguration().then(_ => {
            return this.hasDraftAccess(orderTypeID);
        })
    }

    //Any Order Types within the group have Draft Access
    canDraftOrderTypeGroup(orderTypeGroup: eOrderTypeGroup): Promise<boolean> {
        let draftAccessLevels: eAccessLevel[] = [eAccessLevel.DraftOnly, eAccessLevel.Full];
        return this.getOrderTypeGroupAccessLevels(orderTypeGroup).then(accesses => {
            return (accesses.length == 0 || //No defined Access Levels
                accesses.filter(access => draftAccessLevels.includes(access)).length > 0); //At least draft access level
        });
    }

    canSubmitOrderType(orderTypeID: eOrderType): Promise<boolean> {
        return this.setConfiguration().then(_ => {
            return this.hasSubmitAccess(orderTypeID);
        })
    }

    //Any Order Types within the group have Submit Access
    canSubmittOrderTypeGroup(orderTypeGroup: eOrderTypeGroup): Promise<boolean> {
        let submitAccessLevels: eAccessLevel[] = [eAccessLevel.Full];
        return this.getOrderTypeGroupAccessLevels(orderTypeGroup).then(accesses => {
            return (accesses.length == 0 || //No defined Access Levels
                accesses.filter(access => submitAccessLevels.includes(access)).length > 0); //At least submit access level
        });
    }

    //NOTE: Synchronous methed because the underlying value is not from the databse/api
    getCurrentDashboardFilter(): string {
        return this._currentDashboardFilter;
    }

    //NOTE: Synchronous methed because the underlying value is not from the databse/api
    setCurrentDashboardFilter(currentDashboardFilter: string) {
        this._currentDashboardFilter = currentDashboardFilter;
    }

    //NOTE: Synchronous methed because the underlying value is not from the databse/api
    getCurrentDashboardLabel(): string {
        return this._currentDashboardLabel;
    }

    //NOTE: Synchronous methed because the underlying value is not from the databse/api
    setCurrentDashboardLabel(currentDashboardFilter: string) {
        this._currentDashboardLabel = currentDashboardFilter;
    }

    //WARNING: This method only works if you have already populated the values from the API in some way. 
    //This only exists to avoid some issues in the Order Stepper.
    shouldShowRegisteringAgentTab(): boolean {
        return this._hasRegisteringAgentTabVisibility;
    }

    //WARNING: This method only works if you have already populated the values from the API in some way. 
    //This only exists to avoid some issues in the Order Actions.
    hasViewAccess(orderTypeID: eOrderType): boolean {
        return this.hasSufficientAccessLevel(orderTypeID, [eAccessLevel.ViewOnly, eAccessLevel.DraftOnly, eAccessLevel.Full]);
    }

    //WARNING: This method only works if you have already populated the values from the API in some way. 
    //This only exists to avoid some issues in the Order Actions.
    hasDraftAccess(orderTypeID: eOrderType): boolean {
        return this.hasSufficientAccessLevel(orderTypeID, [eAccessLevel.DraftOnly, eAccessLevel.Full]);
    }

    //WARNING: This method only works if you have already populated the values from the API in some way. 
    //This only exists to avoid some issues in the Order Actions.
    hasSubmitAccess(orderTypeID: eOrderType): boolean {
        return this.hasSufficientAccessLevel(orderTypeID, [eAccessLevel.Full]);
    }

    hasSufficientAccessLevel(orderTypeID: eOrderType, accessLevels: eAccessLevel[]): boolean {
        let accessLevel = this._orderTypeAccessLevels.find(access => access.orderTypeID == orderTypeID)?.accessLevelID ?? eAccessLevel.Full;

        return accessLevels.includes(accessLevel);
    }
}